import React, {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import IgeaProgressCard from './IgeaProgressCard';
import IgeaModuleAccordion from './IgeaModuleAccordion';
import IgeaSidebarFooter from './IgeaSidebarFooter';

/**
 * IGEA: Sidebar — Course Content card + modules accordion.
 * Width: 280px. Flex column with gap. White cards with rounded corners.
 *
 * Note: Learners + Discuss tabs were removed (not in MVP scope). Re-add
 * them here when the features land — see git history of this file for
 * the original Path/Learners/Discuss tab bar.
 */
const IgeaSidebar = ({
  courseTitle,
  courseDescription,
  totalLessons = 0,
  completedLessons = 0,
  modules = [],
  currentUnitId,
  propedeuticita,
}) => {
  // Un modulo aperto per volta (richiesta cliente: "quando clicco su un modulo
  // deve chiudere gli altri aperti"). Lo stato sta qui e non nei singoli
  // accordion perche' e' una proprieta' della lista, non del modulo: prima
  // ognuno teneva il proprio `open` e nessuno sapeva degli altri.
  const moduloCorrente = useMemo(() => (
    modules.find((m) => m.lessons?.some((l) => l.id === currentUnitId))?.id ?? null
  ), [modules, currentUnitId]);
  const primoApribile = useMemo(() => (
    modules.find((m) => !m.locked)?.id ?? null
  ), [modules]);

  // `undefined` = nessuna scelta dell'utente ancora, si usa il default
  // calcolato (il modulo della lezione corrente, altrimenti il primo aperto).
  // `null` = l'utente ha chiuso tutto, ed e' una scelta da rispettare.
  const [sceltaUtente, setSceltaUtente] = useState(undefined);
  const idAperto = sceltaUtente === undefined
    ? (moduloCorrente ?? primoApribile)
    : sceltaUtente;

  // Cambiando lezione si apre il modulo che la contiene, e gli altri si
  // chiudono: e' l'unico modo per cui l'elenco mostra sempre dove sei.
  // Vale anche quando si resta nello stesso modulo dopo averlo chiuso a mano
  // — dipende da `currentUnitId`, non solo dal modulo — perche' altrimenti si
  // navigherebbe alla cieca fra soli titoli.
  useEffect(() => {
    if (moduloCorrente) { setSceltaUtente(moduloCorrente); }
  }, [moduloCorrente, currentUnitId]);

  // Cliccare il modulo aperto lo richiude: nessun elemento a scomparsa che
  // non risponde al clic. Quello che si perde chiudendolo e' recuperabile a
  // colpo d'occhio — a modulo chiuso l'anello dice a che punto sei, e il
  // titolo della lezione in corso resta nell'intestazione del player.
  const alClic = (id) => setSceltaUtente(idAperto === id ? null : id);

  // Segnalazione cliente (2026-08-27): "quando vado avanti nel player cambia
  // correttamente lezione ma lo scroll della sidebar non mi porta sulla lezione
  // attiva". Misurato su un corso da 12 moduli x 5 lezioni in una finestra da
  // 1280x800: l'elenco e' alto 898px in una finestra di 534px, e riprendendo da
  // "Lezione 53" la riga attiva stava a 805-883px con `scrollTop` a 0 — cioe'
  // 81px sotto il bordo inferiore, mai raggiunta da nessuno scorrimento.
  const contenitore = useRef(null);

  // QUANDO si scorre: solo al cambio di lezione, e al primo disegno.
  // Le dipendenze sono due stringhe, non `modules`: cosi' il ricalcolo
  // dell'outline che arriva con `igea:unit-completed` (serve a far comparire la
  // spunta) ridisegna la lista ma non muove la barra. Chi sta leggendo l'elenco
  // mentre finisce un video non se lo vede scappare via.
  //
  // `idAperto` c'e' perche' al confine fra due moduli la riga non esiste ancora
  // nel giro in cui cambia `currentUnitId`: il modulo che la contiene viene
  // aperto dall'effetto qui sopra, un disegno dopo. Osservato in prova: subito
  // dopo il passaggio a "Lezione 11" (primo modulo nuovo) la riga attiva non e'
  // nel DOM. Senza questa seconda dipendenza il salto di modulo — il caso
  // peggiore, quello in cui si e' piu' persi — sarebbe l'unico a non funzionare.
  //
  // COME si distingue dallo scorrimento dell'utente: non lo si indovina, lo si
  // rende impossibile da confondere. Muovere la barra a mano non e' una
  // dipendenza di questo effetto, quindi esplorare l'elenco non viene mai
  // interrotto; e aprire un altro modulo cambia `idAperto` ma toglie dal DOM la
  // riga attiva, quindi si esce senza toccare niente. Resta un solo momento in
  // cui la barra si muove da sola: quando la lezione mostrata e' cambiata — per
  // clic, per pulsante "successiva" o per avanzamento automatico. In quel
  // momento ri-ancorare non e' contraddire l'utente, e' rispondere al
  // cambiamento che ha appena chiesto.
  useLayoutEffect(() => {
    const box = contenitore.current;
    if (!box) { return; }
    const riga = box.querySelector('[aria-current="page"]');
    if (!riga) { return; }

    const finestra = box.getBoundingClientRect();
    const posizione = riga.getBoundingClientRect();
    // Se la riga si vede gia' per intero non si tocca niente: uno scorrimento
    // che non serve resta comunque un movimento che distrae.
    if (posizione.top >= finestra.top && posizione.bottom <= finestra.bottom) { return; }

    // Si scrive `scrollTop` del solo riquadro dei moduli. NON `scrollIntoView`:
    // quello risale tutti gli antenati scorrevoli e riporterebbe in cima la
    // pagina che incornicia il player — e' la stessa famiglia del rimbalzo gia'
    // pagato una volta, quando lo scorrimento del contenuto della lezione
    // faceva saltare su la pagina.
    //
    // La riga finisce al centro della finestra invece che appena dentro il
    // bordo: cosi' si vede anche cosa viene prima e cosa viene dopo, che e' il
    // punto di "sapere dove sono". Il valore viene poi tenuto dentro i limiti
    // reali, altrimenti in fondo all'elenco si chiederebbe uno scorrimento che
    // non esiste.
    const centrata = box.scrollTop
      + (posizione.top - finestra.top)
      - (box.clientHeight - posizione.height) / 2;
    box.scrollTop = Math.max(0, Math.min(centrata, box.scrollHeight - box.clientHeight));
    // Nessun `focus()`: portare in vista non e' spostare il fuoco. Chi naviga
    // col tab sta magari a meta' elenco, e ritrovarsi il cursore altrove
    // perche' il video e' avanzato da solo sarebbe peggio del difetto.
  }, [currentUnitId, idAperto]);

  // Su schermo stretto (sotto `md`) la barra laterale sta SOPRA il contenuto,
  // nella stessa colonna a schermo intero che non scorre (IgeaPlayerLayout,
  // `tw-h-screen tw-overflow-hidden`). Con l'indice sempre aperto si prendeva
  // quasi tutta l'altezza e alla lezione restava una fessura (misurata su
  // dev a 390px: 115px per un questionario alto 595 — igea-frontend #239,
  // «alcune parti vengono tagliate»). Qui l'indice parte chiuso e si apre da
  // un pulsante; aprire una lezione lo richiude. Da `md` in su niente cambia.
  // Aprire una lezione cambia `currentUnitId`: e' quello a richiudere
  // l'indice, non un ascoltatore di clic sul riquadro (che non sarebbe
  // raggiungibile da tastiera).
  const [indiceAperto, setIndiceAperto] = useState(false);
  useEffect(() => { setIndiceAperto(false); }, [currentUnitId]);

  return (
    <aside
      className="tw-w-full md:tw-w-[340px] tw-flex-shrink-0 tw-flex tw-flex-col tw-min-h-0 tw-bg-white tw-rounded-igea tw-overflow-hidden tw-shadow-igea-card"
    >
      {/* Header: Course Content + Progress.
          One single padded section — no inner card chrome, no divider line.
          The progress bar inside this card already separates it from the
          accordion below. */}
      <div className="tw-px-4 tw-pt-4 tw-pb-4">
        <IgeaProgressCard
          title={courseTitle || 'Contenuto del corso'}
          description={courseDescription || ''}
          completed={completedLessons}
          total={totalLessons}
          propedeuticita={propedeuticita}
        />
        {modules.length > 0 && (
          <button
            type="button"
            className="md:tw-hidden tw-mt-3 tw-w-full tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-igea-border tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-text"
            aria-expanded={indiceAperto}
            aria-controls="igea-indice-lezioni"
            data-testid="igea-indice-toggle"
            onClick={() => setIndiceAperto((v) => !v)}
          >
            <span>Indice delle lezioni</span>
            <span aria-hidden="true">{indiceAperto ? '▲' : '▼'}</span>
          </button>
        )}
      </div>

      {/* Modules accordion: scrollable region between header and footer.
          Sotto `md` compare solo con l'indice aperto, e non oltre meta'
          schermo: il resto e' della lezione. */}
      <div
        ref={contenitore}
        id="igea-indice-lezioni"
        data-igea-scroll="moduli"
        className={`${indiceAperto ? 'tw-block tw-max-h-[55vh]' : 'tw-hidden'} md:tw-block md:tw-max-h-none md:tw-flex-1 tw-overflow-y-auto`}
      >
        {modules.length === 0 && (
          <div className="tw-p-4 tw-text-center tw-text-sm tw-text-text-muted">
            Nessun modulo disponibile
          </div>
        )}
        {modules.map((mod, idx) => (
          <IgeaModuleAccordion
            key={mod.id}
            number={String(idx + 1).padStart(2, '0')}
            module={mod}
            currentUnitId={currentUnitId}
            open={idAperto === mod.id}
            onToggle={() => alClic(mod.id)}
          />
        ))}
      </div>

      {/* Footer: lesson/module counters + support CTA (sotto `md` solo a indice aperto) */}
      {modules.length > 0 && (
        <div className={`${indiceAperto ? 'tw-block' : 'tw-hidden'} md:tw-block`}>
          <IgeaSidebarFooter
            totalLessons={totalLessons}
            totalModules={modules.length}
          />
        </div>
      )}
    </aside>
  );
};

IgeaSidebar.propTypes = {
  courseTitle: PropTypes.string,
  courseDescription: PropTypes.string,
  totalLessons: PropTypes.number,
  completedLessons: PropTypes.number,
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      lessons: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          title: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
  ),
  currentUnitId: PropTypes.string,
  // Vero solo per le iscrizioni in modalita' ECM — vedi IgeaProgressCard,
  // che decide se mostrare l'indicazione.
  propedeuticita: PropTypes.bool,
};

IgeaSidebar.defaultProps = {
  courseTitle: '',
  courseDescription: '',
  totalLessons: 0,
  completedLessons: 0,
  modules: [],
  currentUnitId: null,
  propedeuticita: false,
};

export default IgeaSidebar;
